import React, { useState, useEffect, useContext } from 'react';
import { 
  Container, Row, Col, Card, Badge, Button, Table, 
  Modal, Form, Alert, Spinner, Nav, Navbar, NavDropdown,
  Tabs, Tab, ProgressBar, InputGroup
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers, faBrain, faChartLine, faUserMd, faClock,
  faCheck, faTimes, faBan, faUnlock, faEye, faEdit,
  faDollarSign, faCalendar, faExclamationTriangle,
  faArrowUp, faArrowDown, faUserPlus, faFileAlt,
  faSearch, faUserShield, faUserCog, faBell, faPaperPlane, faPeopleGroup
} from '@fortawesome/free-solid-svg-icons';
import Header from '../components/header';
import Footer from '../components/footer';
import RevenueDashboard from '../components/RevenueDashboard';
import { MyContext } from '../MainContext';
import { backendUrl } from '../utils/variables';

// Helper function to safely parse dates for Safari compatibility
const parseDate = (dateString) => {
  if (!dateString) return null;
  
  // Replace space with 'T' for ISO format compatibility
  const isoString = dateString.replace(' ', 'T');
  
  try {
    return new Date(isoString);
  } catch (error) {
    console.error('Error parsing date:', dateString, error);
    return null;
  }
};

const Dashboard = () => {
  const { user, adminData } = useContext(MyContext);
  
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [applications, setApplications] = useState([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [showSubadminModal, setShowSubadminModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [subadminSearch, setSubadminSearch] = useState('');
  const [subadminUsers, setSubadminUsers] = useState([]);
  const [searchingSubadmins, setSearchingSubadmins] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationBody, setNotificationBody] = useState('');
  const [sendingNotification, setSendingNotification] = useState(false);
  const [notificationStats, setNotificationStats] = useState(null);
  const [adminGroups, setAdminGroups] = useState([]);

  // Check if user has admin access (allowlist via MainContext)
  const isAdmin = !!(adminData && adminData.role);
  const canSendNotifications = adminData?.permissions?.notif || false;
  const canViewRevenue = adminData?.permissions?.analytics || false;
  const canManageSubAdmins = adminData?.permissions?.subadmins || false;

  useEffect(() => {
    if (user && isAdmin) {
      loadDashboardData();
      loadNotificationStats();
    }
  }, [user, isAdmin]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load stats
      const statsResponse = await fetch(`${backendUrl}/dashboard_stats.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email })
      });
      const statsData = await statsResponse.json();

      if (statsData.success) {
        setStats(statsData.stats);
      }

      // Load users
      const usersResponse = await fetch(`${backendUrl}/manage_users.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email })
      });
      const usersData = await usersResponse.json();

      if (usersData.success) {
        setUsers(usersData.users);
      }

      // Load applications
      const appsResponse = await fetch(`${backendUrl}/manage_therapist_applications.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email })
      });
      const appsData = await appsResponse.json();

      console.log(appsData);

      if (appsData.success) {
        setApplications(appsData.applications);
      }

      try {
        const groupsResponse = await fetch(`${backendUrl}/admin_list_groups.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: user.email })
        });
        const groupsData = await groupsResponse.json();
        if (groupsData.success) {
          setAdminGroups(groupsData.groups || []);
        }
      } catch (groupsErr) {
        console.warn('Could not load admin groups list:', groupsErr);
      }

    } catch (err) {
      setError('Failed to load dashboard data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUserAction = async (userId, action, reason = '') => {
    try {
      setActionLoading(true);
      
      const response = await fetch(`${backendUrl}/manage_users.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, action, reason, email: user.email })
      });

      const data = await response.json();
      
      if (data.success) {
        loadDashboardData();
        setShowUserModal(false);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Action failed: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleApplicationAction = async (applicationId, action, reason = '') => {
    try {
      setActionLoading(true);
      
      const response = await fetch(`${backendUrl}/manage_therapist_applications.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ application_id: applicationId, action, reason, email: user.email })
      });

      const data = await response.json();
      
      if (data.success) {
        loadDashboardData();
        setShowApplicationModal(false);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Action failed: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const searchSubadmins = async () => {
    try {
      setSearchingSubadmins(true);
      
      const response = await fetch(`${backendUrl}/manage_subadmins.php?search=${encodeURIComponent(subadminSearch)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email })
      });
      const data = await response.json();
      
      if (data.success) {
        setSubadminUsers(data.users);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Search failed: ' + err.message);
    } finally {
      setSearchingSubadmins(false);
    }
  };

  const handleSubadminAction = async (userId, action) => {
    try {
      setActionLoading(true);
      
      const response = await fetch(`${backendUrl}/manage_subadmins.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, action, email: user.email })
      });

      const data = await response.json();
      
      if (data.success) {
        searchSubadmins(); // Refresh the list
        setError(null);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Action failed: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      'pending': 'warning',
      'approved': 'success',
      'rejected': 'danger',
      'active': 'danger',
      'lifted': 'secondary'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const loadNotificationStats = async () => {
    try {
      const response = await fetch(`${backendUrl}/get_fcm_stats.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email })
      });
      const data = await response.json();
      
      if (data.success) {
        setNotificationStats(data.stats);
      }
    } catch (error) {
      console.error('Failed to load notification stats:', error);
    }
  };

  const handleSendNotification = async () => {
    if (!notificationTitle.trim() || !notificationBody.trim()) {
      setError('Please fill in both title and message');
      return;
    }

    try {
      setSendingNotification(true);
      setError(null);

      const response = await fetch(`${backendUrl}/send_firebase_notification.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: notificationTitle.trim(),
          body: notificationBody.trim(),
          url: '/',
          sent_by: user.email
        })
      });

      const result = await response.json();
      
      if (result.success) {
        setShowNotificationModal(false);
        setNotificationTitle('');
        setNotificationBody('');
        setError(null);
        // Reload stats to show updated counts
        loadNotificationStats();
      } else {
        setError(result.message || 'Failed to send notification');
      }
    } catch (error) {
      setError('Failed to send notification: ' + error.message);
    } finally {
      setSendingNotification(false);
    }
  };

  if (!user || !isAdmin) {
    return (
      <>
        <Header />
        <Container className="py-5">
          <Alert variant="danger">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            Access denied. Admin privileges required.
          </Alert>
        </Container>
        <Footer />
      </>
    );
  }

  if (loading) {
    return (
      <>
        <Header />
        <Container className="py-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading dashboard...</p>
        </Container>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col>
            <h2>
              <FontAwesomeIcon icon={faChartLine} className="me-2 text-primary" />
              Admin Dashboard
            </h2>
            <p className="text-muted">Manage users, applications, and view analytics</p>
          </Col>
        </Row>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
          <Tab eventKey="overview" title="Overview">
            {stats && (
              <Row>
                <Col md={3} className="mb-4">
                  <Card className="text-center">
                    <Card.Body>
                      <FontAwesomeIcon icon={faUsers} size="2x" className="text-primary mb-2" />
                      <h4>{stats.total.total_users}</h4>
                      <p className="text-muted">Total Users</p>
                      <small className="text-success">
                        <FontAwesomeIcon icon={faArrowUp} /> +{stats.today.signups_today} today
                      </small>
                    </Card.Body>
                  </Card>
                </Col>
                
                <Col md={3} className="mb-4">
                  <Card className="text-center">
                    <Card.Body>
                      <FontAwesomeIcon icon={faUserMd} size="2x" className="text-success mb-2" />
                      <h4>{stats.total.total_therapists}</h4>
                      <p className="text-muted">Therapists</p>
                      <small className="text-warning">
                        {stats.total.pending_applications} pending
                      </small>
                    </Card.Body>
                  </Card>
                </Col>
                
                <Col md={3} className="mb-4">
                  <Card className="text-center">
                    <Card.Body>
                      <FontAwesomeIcon icon={faFileAlt} size="2x" className="text-info mb-2" />
                      <h4>{stats.today.insights_today}</h4>
                      <p className="text-muted">Insights Today</p>
                      <small className="text-info">
                        {stats.week.insights_week} this week
                      </small>
                    </Card.Body>
                  </Card>
                </Col>
                
                <Col md={3} className="mb-4">
                  <Card className="text-center">
                    <Card.Body>
                      <FontAwesomeIcon icon={faDollarSign} size="2x" className="text-warning mb-2" />
                      <h4>{(stats.revenue?.total_revenue || 0).toLocaleString()} Coins</h4>
                      <p className="text-muted">Total Revenue</p>
                      <small className="text-success">
                        {(stats.revenue?.revenue_today || 0).toLocaleString()} Coins today
                      </small>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}
          </Tab>

          <Tab eventKey="users" title="User Management">
            <Card>
              <Card.Header>
                <h5 className="mb-0">User Management</h5>
              </Card.Header>
              <Card.Body>
                <Table responsive striped>
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Joined</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.user_id}>
                        <td>{user.display_name}</td>
                        <td>{user.email}</td>
                        <td>
                          {user.application_status === 'approved' ? (
                            <Badge bg="success">Therapist</Badge>
                          ) : user.application_status === 'pending' ? (
                            <Badge bg="warning">Pending</Badge>
                          ) : user.application_status === 'rejected' ? (
                            <Badge bg="danger">Rejected</Badge>
                          ) : (
                            <Badge bg="secondary">User</Badge>
                          )}
                        </td>
                        <td>
                          {user.suspension_status ? (
                            getStatusBadge(user.suspension_status)
                          ) : (
                            <Badge bg="success">Active</Badge>
                          )}
                        </td>
                        <td>{parseDate(user.reg_date)?.toLocaleDateString() || 'Invalid date'}</td>
                        <td>
                          <Button
                            size="sm"
                            variant={user.suspension_status === 'active' ? 'success' : 'danger'}
                            onClick={() => {
                              setSelectedUser(user);
                              setShowUserModal(true);
                            }}
                          >
                            <FontAwesomeIcon 
                              icon={user.suspension_status === 'active' ? faUnlock : faBan} 
                              className="me-1" 
                            />
                            {user.suspension_status === 'active' ? 'Lift' : 'Suspend'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="groups" title="Groups">
            <Card>
              <Card.Header>
                <h5 className="mb-0">
                  <FontAwesomeIcon icon={faPeopleGroup} className="me-2" />
                  Groups
                </h5>
              </Card.Header>
              <Card.Body>
                <Table responsive striped>
                  <thead>
                    <tr>
                      <th>Group</th>
                      <th>Type</th>
                      <th>Created by</th>
                      <th>Date created</th>
                      <th>Members</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminGroups.map((g) => (
                      <tr key={g.group_id}>
                        <td>
                          <strong>{g.group_name}</strong>
                          {g.creator_email && (
                            <>
                              <br />
                              <small className="text-muted">{g.creator_email}</small>
                            </>
                          )}
                        </td>
                        <td>{g.group_type || '—'}</td>
                        <td>{(g.user_name && g.user_name.trim()) || '—'}</td>
                        <td>{parseDate(g.created_at)?.toLocaleString() || '—'}</td>
                        <td>{Number(g.member_count) || 0}</td>
                        <td>
                          {String(g.is_active) === '1' || g.is_active === true ? (
                            <Badge bg="success">Active</Badge>
                          ) : (
                            <Badge bg="secondary">Inactive</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {adminGroups.length === 0 && (
                  <p className="text-muted mb-0">No groups found.</p>
                )}
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="subadmins" title="Subadmin Management">
            <Card>
              <Card.Header>
                <h5 className="mb-0">Subadmin Management</h5>
              </Card.Header>
              <Card.Body>
                <Row className="mb-3">
                  <Col md={6}>
                    <InputGroup>
                      <Form.Control
                        type="text"
                        placeholder="Search users by name or email..."
                        value={subadminSearch}
                        onChange={(e) => setSubadminSearch(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && searchSubadmins()}
                      />
                      <Button 
                        variant="primary" 
                        onClick={searchSubadmins}
                        disabled={searchingSubadmins}
                      >
                        {searchingSubadmins ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <FontAwesomeIcon icon={faSearch} />
                        )}
                      </Button>
                    </InputGroup>
                  </Col>
                </Row>

                <Table responsive striped>
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Email</th>
                      <th>Current Role</th>
                      <th>Joined</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subadminUsers.map((user) => (
                      <tr key={user.user_id}>
                        <td>{user.display_name}</td>
                        <td>{user.email}</td>
                        <td>
                          {user.admin_role ? (
                            <Badge bg="info">{user.admin_role}</Badge>
                          ) : (
                            <Badge bg="secondary">User</Badge>
                          )}
                        </td>
                        <td>{parseDate(user.reg_date)?.toLocaleDateString() || 'Invalid date'}</td>
                        <td>
                          {user.admin_role === 'sub_admin' ? (
                            <Button
                              size="sm"
                              variant="danger"
                              onClick={() => handleSubadminAction(user.user_id, 'remove')}
                              disabled={actionLoading}
                            >
                              <FontAwesomeIcon icon={faTimes} className="me-1" />
                              Remove Access
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="success"
                              onClick={() => handleSubadminAction(user.user_id, 'add')}
                              disabled={actionLoading}
                            >
                              <FontAwesomeIcon icon={faUserShield} className="me-1" />
                              Add as Subadmin
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="applications" title="Therapist Applications">
            <Card>
              <Card.Header>
                <h5 className="mb-0">Therapist Applications</h5>
              </Card.Header>
              <Card.Body>
                <Table responsive striped>
                  <thead>
                    <tr>
                      <th>Applicant</th>
                      <th>Price/Session</th>
                      <th>Status</th>
                      <th>Applied</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {applications.map((app) => (
                      <tr key={app.id}>
                        <td>
                          <div>
                            <strong>{app.display_name}</strong>
                            <br />
                            <small className="text-muted">{app.email}</small>
                          </div>
                        </td>
                        <td>{app.price_per_session.toLocaleString()} Coins</td>
                        <td>{getStatusBadge(app.status)}</td>
                        <td>{parseDate(app.created_at)?.toLocaleDateString() || 'Invalid date'}</td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="me-1"
                            onClick={() => {
                              setSelectedApplication(app);
                              setShowApplicationModal(true);
                            }}
                          >
                            <FontAwesomeIcon icon={faEye} className="me-1" />
                            View Details
                          </Button>
                          {/*if status is pending, show approve and reject buttons, if status is approved, show option to reject, if reject show option to accept. details button should be always visible*/}
                          {app.status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                variant="success"
                                className="me-1"
                                onClick={() => handleApplicationAction(app.id, 'approve')}
                                disabled={actionLoading}
                              >
                                <FontAwesomeIcon icon={faCheck} className="me-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="danger"
                                onClick={() => {
                                  setSelectedApplication(app);
                                  setRejectionReason('');
                                  setShowRejectionModal(true);
                                }}
                                disabled={actionLoading}
                              >
                                <FontAwesomeIcon icon={faTimes} className="me-1" />
                                Reject
                              </Button>
                            </>
                          )}
                          {app.status === 'approved' && (
                            <>
                              <Button
                                size="sm"
                                variant="danger"
                                onClick={() => handleApplicationAction(app.id, 'reject')}
                                disabled={actionLoading}
                              >
                                <FontAwesomeIcon icon={faTimes} className="me-1" />
                                Reject
                              </Button>
                            </>
                          )}
                          {app.status === 'rejected' && (
                            <>
                              <Button
                                size="sm"
                                variant="success"
                                onClick={() => handleApplicationAction(app.id, 'approve')}
                                disabled={actionLoading}
                              >
                                <FontAwesomeIcon icon={faCheck} className="me-1" />
                                Accept
                              </Button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="revenue" title="Revenue Dashboard">
            <RevenueDashboard />
          </Tab>

          {canSendNotifications && (
          <Tab eventKey="notifications" title="Push Notifications">
            <Card>
              <Card.Header>
                <h5 className="mb-0">
                  <FontAwesomeIcon icon={faBell} className="me-2" />
                  Push Notifications
                </h5>
              </Card.Header>
              <Card.Body>
                {notificationStats && (
                  <Row className="mb-4">
                    <Col md={4}>
                      <Card className="text-center bg-light">
                        <Card.Body>
                          <FontAwesomeIcon icon={faUsers} size="2x" className="text-primary mb-2" />
                          <h4>{notificationStats.total_subscribers || 0}</h4>
                          <p className="text-muted">Total Subscribers</p>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={4}>
                      <Card className="text-center bg-light">
                        <Card.Body>
                          <FontAwesomeIcon icon={faBell} size="2x" className="text-success mb-2" />
                          <h4>{notificationStats.active_tokens || 0}</h4>
                          <p className="text-muted">Active Tokens</p>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={4}>
                      <Card className="text-center bg-light">
                        <Card.Body>
                          <FontAwesomeIcon icon={faPaperPlane} size="2x" className="text-info mb-2" />
                          <h4>{notificationStats.notifications_sent || 0}</h4>
                          <p className="text-muted">Notifications Sent</p>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                )}

                <div className="text-center">
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={() => setShowNotificationModal(true)}
                  >
                    <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                    Send Push Notification
                  </Button>
                  <p className="text-muted mt-2">
                    Send a notification to all subscribed users
                  </p>
                </div>

                <div className="mt-4">
                  <h6>Notification Guidelines:</h6>
                  <ul className="text-muted">
                    <li>Keep titles short and engaging (under 50 characters)</li>
                    <li>Write clear, actionable messages</li>
                    <li>Use notifications for important updates only</li>
                    <li>Test notifications before sending to all users</li>
                  </ul>
                </div>
              </Card.Body>
            </Card>
          </Tab>
          )}
        </Tabs>
      </Container>

      {/* User Action Modal */}
      <Modal show={showUserModal} onHide={() => setShowUserModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedUser?.suspension_status === 'active' ? 'Lift Suspension' : 'Suspend User'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {selectedUser?.suspension_status === 'active' 
              ? `Are you sure you want to lift the suspension for ${selectedUser?.display_name}?`
              : `Are you sure you want to suspend ${selectedUser?.display_name}?`
            }
          </p>
          <Form>
            <Form.Group>
              <Form.Label>Reason</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter reason for this action..."
                id="actionReason"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUserModal(false)}>
            Cancel
          </Button>
          <Button
            variant={selectedUser?.suspension_status === 'active' ? 'success' : 'danger'}
            onClick={() => {
              const reason = document.getElementById('actionReason').value;
              handleUserAction(
                selectedUser.user_id,
                selectedUser.suspension_status === 'active' ? 'lift' : 'suspend',
                reason
              );
            }}
            disabled={actionLoading}
          >
            {actionLoading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              selectedUser?.suspension_status === 'active' ? 'Lift Suspension' : 'Suspend User'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Application Review Modal */}
      <Modal show={showApplicationModal} onHide={() => setShowApplicationModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faUserMd} className="me-2 text-primary" />
            Application Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedApplication && (
            <div>
              <Row>
                <Col md={4}>
                  {selectedApplication.photo_url ? (
                    <img 
                      src={`${backendUrl}/${selectedApplication.photo_url}`} 
                      alt="Profile" 
                      className="img-fluid rounded shadow-sm"
                      style={{ maxHeight: '200px', objectFit: 'cover' }}
                    />
                  ) : (
                    <div className="bg-light rounded p-4 text-center shadow-sm">
                      <FontAwesomeIcon icon={faUserMd} size="3x" className="text-muted" />
                      <p className="text-muted mt-2">No photo uploaded</p>
                    </div>
                  )}
                </Col>
                <Col md={8}>
                  <div className="mb-3">
                    <h5 className="mb-1">{selectedApplication.display_name}</h5>
                    <p className="text-muted mb-2">{selectedApplication.email}</p>
                    <Badge 
                      bg={selectedApplication.status === 'pending' ? 'warning' : 
                          selectedApplication.status === 'approved' ? 'success' : 'danger'}
                      className="mb-2"
                    >
                      {selectedApplication.status === 'pending' ? 'Under Review' : 
                       selectedApplication.status === 'approved' ? 'Approved' : 'Rejected'}
                    </Badge>
                  </div>
                  
                  <div className="mb-3">
                    <h6><FontAwesomeIcon icon={faDollarSign} className="me-2 text-success" />Pricing</h6>
                                            <p className="mb-1"><strong>Price per session:</strong> {selectedApplication.price_per_session.toLocaleString()} Coins</p>
                  </div>
                  
                  <div className="mb-3">
                    <h6><FontAwesomeIcon icon={faFileAlt} className="me-2 text-primary" />Professional Bio</h6>
                    <div className="bg-light p-3 rounded">
                      <p className="mb-0">{selectedApplication.bio}</p>
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <h6><FontAwesomeIcon icon={faCalendar} className="me-2 text-info" />Application Timeline</h6>
                    <p className="mb-1"><strong>Applied:</strong> {parseDate(selectedApplication.created_at)?.toLocaleString() || 'Invalid date'}</p>
                    {selectedApplication.reviewed_at && (
                      <p className="mb-1"><strong>Reviewed:</strong> {parseDate(selectedApplication.reviewed_at)?.toLocaleString() || 'Invalid date'}</p>
                    )}
                    {selectedApplication.reviewer_name && (
                      <p className="mb-1"><strong>Reviewed by:</strong> {selectedApplication.reviewer_name}</p>
                    )}
                  </div>
                  
                  {selectedApplication.status === 'rejected' && selectedApplication.rejection_reason && (
                    <div className="mb-3">
                      <h6><FontAwesomeIcon icon={faTimes} className="me-2 text-danger" />Rejection Reason</h6>
                      <div className="bg-light p-3 rounded border-start border-danger border-3">
                        <p className="mb-0 text-danger">{selectedApplication.rejection_reason}</p>
                      </div>
                    </div>
                  )}
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowApplicationModal(false)}>
            Close
          </Button>
          {selectedApplication?.status === 'pending' && (
            <>
              <Button
                variant="danger"
                onClick={() => {
                  setRejectionReason('');
                  setShowRejectionModal(true);
                }}
                disabled={actionLoading}
              >
                {actionLoading ? <Spinner animation="border" size="sm" /> : 'Reject'}
              </Button>
              <Button
                variant="success"
                onClick={() => handleApplicationAction(selectedApplication.id, 'approve')}
                disabled={actionLoading}
              >
                {actionLoading ? <Spinner animation="border" size="sm" /> : 'Approve'}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {/* Rejection Reason Modal */}
      <Modal show={showRejectionModal} onHide={() => setShowRejectionModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faTimes} className="me-2 text-danger" />
            Reject Application
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            You are about to reject the application from <strong>{selectedApplication?.display_name}</strong>.
            Please provide a reason for the rejection.
          </p>
          <Form>
            <Form.Group>
              <Form.Label>Rejection Reason</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Enter the reason for rejecting this application..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                required
              />
              <Form.Text className="text-muted">
                This reason will be shown to the applicant.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRejectionModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              if (rejectionReason.trim()) {
                handleApplicationAction(selectedApplication.id, 'reject', rejectionReason.trim());
                setShowRejectionModal(false);
                setRejectionReason('');
              }
            }}
            disabled={actionLoading || !rejectionReason.trim()}
          >
            {actionLoading ? <Spinner animation="border" size="sm" /> : 'Reject Application'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Send Notification Modal */}
      <Modal show={showNotificationModal} onHide={() => setShowNotificationModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faPaperPlane} className="me-2 text-primary" />
            Send Push Notification
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Notification Title *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter notification title..."
                value={notificationTitle}
                onChange={(e) => setNotificationTitle(e.target.value)}
                maxLength={50}
              />
              <Form.Text className="text-muted">
                Keep it short and engaging. {notificationTitle.length}/50 characters
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Notification Message *</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Enter notification message..."
                value={notificationBody}
                onChange={(e) => setNotificationBody(e.target.value)}
                maxLength={200}
              />
              <Form.Text className="text-muted">
                Clear, actionable message. {notificationBody.length}/200 characters
              </Form.Text>
            </Form.Group>

            <Alert variant="info">
              <FontAwesomeIcon icon={faBell} className="me-2" />
              <strong>Important:</strong> This notification will be sent to all users who have enabled push notifications.
              Make sure the content is relevant and valuable to your users.
            </Alert>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNotificationModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSendNotification}
            disabled={sendingNotification || !notificationTitle.trim() || !notificationBody.trim()}
          >
            {sendingNotification ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Sending...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                Send Notification
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Footer />
    </>
  );
};

export default Dashboard; 